<template>
  <div class="flex min-h-screen flex-col">
    <Navbar v-model:desktop-sidebar-expanded="desktopSidebarExpanded" />
    <div
      class="flex flex-1 flex-col transition-[padding] duration-200"
      :class="desktopSidebarExpanded ? 'lg:pl-60' : 'lg:pl-20'"
    >
      <main class="flex-1">
        <router-view />
      </main>
    </div>
    <Transition name="scroll-top">
      <button
        v-if="showScrollTop && route.name !== 'chats'"
        class="scroll-top-button"
        :style="{ '--scroll-progress-deg': `${scrollProgress * 3.6}deg` }"
        type="button"
        aria-label="Scroll to top"
        title="Scroll to top"
        @click="scrollToTop"
      >
        <span class="scroll-top-button-inner">
          <i class="ri-arrow-up-line text-lg" aria-hidden="true"></i>
        </span>
      </button>
    </Transition>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import Navbar from '@/components/Navbar.vue'
import { connectChatRealtime, disconnectChatRealtime } from '@/services/chatRealtime'
import { useSessionStore } from '@/stores/session'

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const SIDEBAR_STORAGE_KEY = 'netflyr.sidebar.expanded'
const getInitialSidebarState = () => {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
}

const desktopSidebarExpanded = ref(getInitialSidebarState())
const showScrollTop = ref(false)
const scrollProgress = ref(0)
const chatMessageEvents = ['message', 'receive_message', 'new_message', 'chat_message', 'send_message']
let notificationSocket = null
let recentNotificationKey = ''
let recentNotificationAt = 0

const firstSocketValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '' && value !== '0') || ''

const unwrapSocketPayload = (payload = {}) => {
  const response = payload?.socket_response && typeof payload.socket_response === 'object'
    ? payload.socket_response
    : payload
  if (Array.isArray(response)) return response[0] || {}
  if (Array.isArray(response?.data)) return response.data[0] || {}
  if (response?.data && typeof response.data === 'object') return response.data
  return response || {}
}

const handleGlobalChatMessage = (payload = {}) => {
  const source = unwrapSocketPayload(payload)
  const message = source.message && typeof source.message === 'object' ? source.message : {}
  const senderId = String(firstSocketValue(
    source.from_id,
    source.sender_id,
    source.userid,
    source.user_id,
    message.from_id,
    message.sender_id,
    message.userid,
    message.user_id
  )).trim()
  const currentUserId = String(session.userId || '').trim()
  if (!senderId || !currentUserId || senderId === currentUserId) return

  const text = String(firstSocketValue(
    source.message_text,
    source.msg,
    source.body,
    typeof source.message === 'string' ? source.message : '',
    message.message,
    message.msg,
    message.body
  )).trim()
  if (!text) return

  const messageId = firstSocketValue(source.id, source.chat_id, source.message_id, message.id, message.chat_id, message.message_id)
  const notificationKey = String(messageId || `${senderId}:${text}`)
  const now = Date.now()
  if (notificationKey === recentNotificationKey && now - recentNotificationAt < 2500) return
  recentNotificationKey = notificationKey
  recentNotificationAt = now

  if (route.name === 'chats') return
  const senderName = String(firstSocketValue(
    source.user_name,
    source.from_user_name,
    source.sender_name,
    source.username,
    message.user_name,
    message.from_user_name,
    message.sender_name,
    'New message'
  )).trim()

  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'info',
    title: senderName,
    text: text.length > 120 ? `${text.slice(0, 117)}...` : text,
    showConfirmButton: true,
    confirmButtonText: 'Open chat',
    showCloseButton: true,
    timer: 7000,
    timerProgressBar: true
  }).then((result) => {
    if (result.isConfirmed) {
      router.push({
        name: 'chats',
        query: {
          to_id: senderId,
          to_name: senderName,
          source: 'user'
        }
      })
    }
  })
}

const detachGlobalChatNotifications = () => {
  if (!notificationSocket) return
  chatMessageEvents.forEach((eventName) => notificationSocket.off(eventName, handleGlobalChatMessage))
  notificationSocket = null
}

const attachGlobalChatNotifications = (socket) => {
  if (!socket || notificationSocket === socket) return
  detachGlobalChatNotifications()
  notificationSocket = socket
  chatMessageEvents.forEach((eventName) => socket.on(eventName, handleGlobalChatMessage))
}

const updateScrollTopVisibility = () => {
  if (typeof window === 'undefined') return
  const scrollableHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
  scrollProgress.value = Math.min(100, Math.max(0, Math.round((window.scrollY / scrollableHeight) * 100)))
  showScrollTop.value = window.scrollY > 320
}

const scrollToTop = () => {
  if (typeof window === 'undefined') return
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
  updateScrollTopVisibility()
  window.addEventListener('scroll', updateScrollTopVisibility, { passive: true })
  window.addEventListener('resize', updateScrollTopVisibility, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScrollTopVisibility)
  window.removeEventListener('resize', updateScrollTopVisibility)
  detachGlobalChatNotifications()
  disconnectChatRealtime()
})

watch(
  () => session.userId,
  (userId) => {
    if (!userId) {
      detachGlobalChatNotifications()
      disconnectChatRealtime()
      return
    }
    attachGlobalChatNotifications(connectChatRealtime(userId))
  },
  { immediate: true }
)

watch(
  desktopSidebarExpanded,
  (value) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value))
    }
  },
  { flush: 'post' }
)
</script>

<style scoped>
.scroll-top-button {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 35;
  display: grid;
  height: 2.65rem;
  width: 2.65rem;
  place-items: center;
  border-radius: 9999px;
  background:
    conic-gradient(rgb(59 130 246) var(--scroll-progress-deg, 0deg), rgb(226 232 240) 0deg);
  color: rgb(37 99 235);
  box-shadow: 0 14px 30px rgb(15 23 42 / 0.16);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.scroll-top-button-inner {
  display: grid;
  height: calc(100% - 0.38rem);
  width: calc(100% - 0.38rem);
  place-items: center;
  border-radius: 9999px;
  background: white;
  color: rgb(37 99 235);
  box-shadow: inset 0 0 0 1px rgb(219 234 254);
  transition: background 0.16s ease, color 0.16s ease;
}

.scroll-top-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgb(15 23 42 / 0.22);
}

.scroll-top-button:hover .scroll-top-button-inner {
  background: rgb(239 246 255);
  color: rgb(29 78 216);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(0.5rem) scale(0.96);
}

@media (min-width: 1024px) {
  .scroll-top-button {
    right: 1.5rem;
    bottom: 1.5rem;
  }
}
</style>
